import { useId } from 'react';
import { dateShort, money } from '../../lib/format.ts';

export interface ChartSeries { name: string; values: { x: number; value: number }[]; tone: 'expense' | 'income' | 'baseline' | 'forecast'; dashed?: boolean }

export function TrendChart({ labels, series, currency, label }: { labels: string[]; series: ChartSeries[]; currency: string; label: string }) {
  const id = useId();
  const width = 640, height = 200, left = 4, right = 4, top = 8, bottom = 8;
  const high = Math.max(1, ...series.flatMap(s => s.values.map(p => p.value)));
  const low = Math.min(0, ...series.flatMap(s => s.values.map(p => p.value)));
  const x = (i: number) => left + i / Math.max(1, labels.length - 1) * (width - left - right);
  const y = (v: number) => height - bottom - (v - low) / (high - low) * (height - bottom - top);
  const ticks = Array.from(new Set([0, Math.floor((labels.length - 1) / 2), labels.length - 1]));
  const axis = (value: number) => {
    const magnitude = Math.abs(value);
    const unit = magnitude >= 1e9 ? 1e9 : magnitude >= 1e6 ? 1e6 : magnitude >= 1e3 ? 1e3 : 1;
    return (value / unit).toLocaleString('ru-RU', { maximumFractionDigits: unit > 1 ? 1 : 0 }) + (unit === 1e9 ? ' млрд' : unit === 1e6 ? ' млн' : unit === 1e3 ? 'к' : '');
  };
  return <figure className="chart report-trend">
    <div className="report-chart-grid">
    <div className="report-y-axis" aria-hidden>{[1, .5, 0].map(p => { const value = low + p * (high - low); return <span key={p} title={money(value, currency)}>{axis(value)}</span>; })}</div>
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby={id}>
      <title id={id}>{label}</title>
      {[0, .5, 1].map(p => <g key={p}><line x1={left} x2={width - right} y1={y(low + p * (high - low))} y2={y(low + p * (high - low))} className="grid" /></g>)}
      {series.map(s => <g key={s.name} className={`report-line report-line-${s.tone}`}>
        <polyline fill="none" strokeWidth="2.5" strokeDasharray={s.dashed ? '7 5' : undefined} points={s.values.map(p => `${x(p.x)},${y(p.value)}`).join(' ')} />
        {s.values.length === 1 ? <circle cx={x(s.values[0].x)} cy={y(s.values[0].value)} r="3" /> : null}
      </g>)}
    </svg>
    <div className="report-x-axis" aria-hidden>{ticks.filter(i => i >= 0).map(i => <span key={i}>{/^\d{4}-\d{2}-\d{2}$/.test(labels[i]) ? dateShort(labels[i]) : labels[i]}</span>)}</div>
    </div>
    {/* the lines are named only: the chart is read by its shape, not by a picked day */}
    <figcaption className="report-chart-legend">{series.map(s => <span key={s.name}><i className={`report-swatch report-swatch-${s.tone}`} />{s.name}</span>)}</figcaption>
  </figure>;
}

export function ReportSkeleton() {
  return <div className="report-loading" aria-label="Загружаю отчёт" aria-busy="true"><div className="skeleton sk-card" /><div className="skeleton sk-card" /></div>;
}
